import { ChatService, ChatMessage } from "./chat-service";

// Icons
const ICON_SEND = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>`;
const ICON_CLIP = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path></svg>`;
const ICON_X = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
const ICON_FILE = `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>`;

export class ChatUI {
  private container: HTMLElement;
  private messagesContainer: HTMLElement;
  private input: HTMLInputElement;
  private fileInput: HTMLInputElement;
  private filePreview: HTMLElement;
  private selectedFile: File | null = null;
  private isOpen = false;

  constructor(private service: ChatService, private parentElement: HTMLElement) {
    this.container = document.createElement("div");
    this.container.id = "chat-panel";
    this.container.className = "chat-panel hidden";

    // --- Header ---
    const header = document.createElement("div");
    header.className = "chat-header";
    header.innerHTML = `<h3>Chat de la consulta</h3>`;
    const closeBtn = document.createElement("button");
    closeBtn.innerHTML = ICON_X;
    closeBtn.onclick = () => this.toggle();
    header.appendChild(closeBtn);
    this.container.appendChild(header);

    // --- Messages List ---
    this.messagesContainer = document.createElement("div");
    this.messagesContainer.className = "chat-messages";
    this.container.appendChild(this.messagesContainer);

    // --- Input Area ---
    const inputArea = document.createElement("div");
    inputArea.className = "chat-input-area";

    // File Preview
    this.filePreview = document.createElement("div");
    this.filePreview.className = "file-preview hidden";
    inputArea.appendChild(this.filePreview);

    // Form
    const form = document.createElement("form");
    form.className = "chat-form";
    
    // File Input (Hidden)
    this.fileInput = document.createElement("input");
    this.fileInput.type = "file";
    this.fileInput.style.display = "none";
    this.fileInput.onchange = (e: any) => this.handleFileSelect(e.target.files?.[0]);

    // Paperclip Button
    const attachBtn = document.createElement("button");
    attachBtn.type = "button";
    attachBtn.className = "icon-btn";
    attachBtn.innerHTML = ICON_CLIP;
    attachBtn.onclick = () => this.fileInput.click();

    // Text Input
    this.input = document.createElement("input");
    this.input.placeholder = "Escribe un mensaje...";
    this.input.className = "message-input";

    // Send Button
    const sendBtn = document.createElement("button");
    sendBtn.type = "submit";
    sendBtn.className = "send-btn";
    sendBtn.innerHTML = ICON_SEND;

    form.appendChild(this.fileInput);
    form.appendChild(attachBtn);
    form.appendChild(this.input);
    form.appendChild(sendBtn);
    inputArea.appendChild(form);
    this.container.appendChild(inputArea);

    form.onsubmit = (e) => this.handleSubmit(e);

    this.parentElement.appendChild(this.container);

    this.service.subscribe((messages) => this.renderMessages(messages));
  }

  toggle() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.container.classList.remove("hidden");
      this.service.startPolling();
      this.scrollToBottom();
    } else {
      this.container.classList.add("hidden");
      this.service.stopPolling();
    }

    return this.isOpen;
  }

  private renderMessages(messages: ChatMessage[]) {
    this.messagesContainer.innerHTML = "";
    const myId = this.service.getUserId();

    messages.forEach((msg) => {
      const isMe = msg.senderId === myId;
      const msgEl = document.createElement("div");
      msgEl.className = `chat-message ${isMe ? "me" : "other"}`;

      let contentHtml = "";
      if (msg.text) contentHtml += `<p>${msg.text}</p>`;
      if (msg.fileUrl) {
        contentHtml += `
          <a href="${msg.fileUrl}" target="_blank" class="file-attachment ${isMe ? 'me' : 'other'}">
            ${ICON_FILE}
            <span>${msg.fileName || "Adjunto"}</span>
          </a>
        `;
      }

      msgEl.innerHTML = `
        <div class="bubble">${contentHtml}</div>
        <span class="sender-name">${isMe ? "Tú" : msg.sender.name}</span>
      `;
      this.messagesContainer.appendChild(msgEl);
    });
    this.scrollToBottom();
  }

  private handleFileSelect(file?: File) {
    if (!file) return;
    this.selectedFile = file;
    this.filePreview.innerHTML = `
      <div class="file-tag">
        ${ICON_FILE} <span>${file.name}</span>
        <button type="button" class="remove-file-btn">${ICON_X}</button>
      </div>
    `;
    this.filePreview.querySelector("button")!.onclick = () => {
      this.selectedFile = null;
      this.filePreview.classList.add("hidden");
      this.fileInput.value = "";
    };
    this.filePreview.classList.remove("hidden");
  }

  private async handleSubmit(e: Event) {
    e.preventDefault();
    const text = this.input.value.trim();
    if (!text && !this.selectedFile) return;

    const submitBtn = this.container.querySelector("button[type=submit]") as HTMLButtonElement;
    submitBtn.disabled = true;

    try {
      let fileUrl;
      if (this.selectedFile) {
        fileUrl = await this.service.uploadFile(this.selectedFile);
      }

      await this.service.sendMessage(
        text || (this.selectedFile ? "📎 Adjunto" : ""),
        fileUrl,
        this.selectedFile?.name,
        this.selectedFile?.type
      );

      // Reset
      this.input.value = "";
      this.selectedFile = null;
      this.filePreview.classList.add("hidden");
      this.fileInput.value = "";
    } catch (err) {
      alert("Failed to send message");
      console.error(err);
    } finally {
      submitBtn.disabled = false;
    }
  }

  private scrollToBottom() {
    this.messagesContainer.scrollTop = this.messagesContainer.scrollHeight;
  }
}
